import { getDocs, getDoc, query, where, orderBy, limit, startAfter, Query, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { getCollectionRef, getDocumentRef, handleFirebaseError, uploadImage } from './firebaseUtils';
import { workspaceApi, WorkspaceApiError } from './workspaceApi';
import { WorkspaceScope } from '../types/workspace';
import { Promotion, CreatePromotionRequest, UpdatePromotionRequest, PromotionListResponse, PromotionFilter, PromotionSort, CrudResult } from '../types';
import { getPromotionSlugParts, parsePublicIdentifier } from '../utils/publication.mjs';

export function isCurrentPromotion(scope: WorkspaceScope, data: Partial<Promotion>): boolean {
  if (typeof data.slug !== 'string' || data.workspaceId !== scope.workspaceId || getPromotionSlugParts(data)?.source !== 'current') return false;
  try { return parsePublicIdentifier(data.slug).accountCode === scope.accountCode; }
  catch { return false; }
}
export async function getCurrentPromotionIds(scope: WorkspaceScope): Promise<Set<string>> {
  const snapshot = await getDocs(getCollectionRef(scope, 'promotions'));
  return new Set(snapshot.docs.filter(item => isCurrentPromotion(scope, item.data())).map(item => item.id));
}

async function result<T>(operation: () => Promise<T>): Promise<CrudResult<T>> {
  try { return { success: true, data: await operation() }; }
  catch (error) { return { success: false, error: handleFirebaseError(error) }; }
}
async function prepareImage(scope: WorkspaceScope, imageUrl?: string | null): Promise<string | null | undefined> {
  if (!imageUrl?.startsWith('blob:')) return imageUrl;
  const blob = await (await fetch(imageUrl)).blob();
  return uploadImage(scope, new File([blob], 'promotion-image.jpg', { type: blob.type }), 'promotions');
}
export const checkCodeDuplicate = (scope: WorkspaceScope, code: string, excludePromotionId?: string): Promise<CrudResult<boolean>> => result(async () => {
  const docs = await getDocs(query(getCollectionRef(scope, 'promotions'), where('code', '==', code)));
  return docs.docs.some(item => item.id !== excludePromotionId);
});
export const checkPublicSlugs = (scope: WorkspaceScope, slugs: string[], excludeId?: string): Promise<CrudResult<string[]>> => result(async () => {
  const response = await workspaceApi<{ conflicts: string[] }>('checkSlugs', scope, { slugs, excludeId });
  return response.conflicts;
});
export const checkPublicationDuplicate = (scope: WorkspaceScope, slug: string, excludeId?: string): Promise<CrudResult<boolean>> => result(async () => {
  const response = await checkPublicSlugs(scope, [slug], excludeId);
  if (!response.success) throw new Error(response.error);
  return !!response.data?.length;
});
export const createPromotion = (scope: WorkspaceScope, data: CreatePromotionRequest): Promise<CrudResult<string>> => result(async () => {
  const imageUrl = await prepareImage(scope, data.imageUrl);
  return workspaceApi<string>('savePromotion', scope, { data: { ...data, imageUrl } });
});
export const updatePromotion = (scope: WorkspaceScope, id: string, data: UpdatePromotionRequest): Promise<CrudResult<void>> => result(async () => {
  const imageUrl = await prepareImage(scope, data.imageUrl);
  await workspaceApi('savePromotion', scope, { id, data: { ...data, ...(imageUrl !== undefined ? { imageUrl } : {}) } });
});
export const updatePromotionStatus = (scope: WorkspaceScope, id: string, isActive: boolean): Promise<CrudResult<void>> => result(async () => {
  await workspaceApi('savePromotion', scope, { id, data: { isActive } });
});
export const updatePromotionSlug = (scope: WorkspaceScope, id: string, slug: string): Promise<CrudResult<void>> => result(async () => {
  const parsed = parsePublicIdentifier(slug);
  if (parsed.accountCode !== scope.accountCode) throw new Error('현재 계정의 공개 슬러그를 입력해주세요.');
  await workspaceApi('savePromotion', scope, { id, data: { slug: parsed.slug, randomPrefix: parsed.randomPrefix, userSuffix: parsed.userSuffix } });
});
export const updateShortUrl = (scope: WorkspaceScope, id: string, shortUrl: string): Promise<CrudResult<void>> => result(async () => {
  await workspaceApi('savePromotion', scope, { id, data: { shortUrl: shortUrl.trim() } });
});
export const deletePromotion = (scope: WorkspaceScope, id: string): Promise<CrudResult<void>> => result(async () => {
  await workspaceApi('deletePromotion', scope, { id });
});
export const getPromotionById = (scope: WorkspaceScope, id: string): Promise<CrudResult<Promotion>> => result(async () => {
  if (scope.mode === 'public') {
    const response = await workspaceApi<{ promotion: Promotion }>('publicRelated', scope, { identifier: scope.publicIdentifier, promotionId: id });
    return response.promotion;
  }
  const snapshot = await getDoc(getDocumentRef(scope, 'promotions', id));
  if (!snapshot.exists()) throw new Error('프로모션을 찾을 수 없습니다.');
  if (!isCurrentPromotion(scope, snapshot.data())) throw new Error('이전 형식의 시험 데이터입니다. 새 프로모션을 등록해주세요.');
  return { ...snapshot.data(), id: snapshot.id } as Promotion;
});
export const getPromotions = async (
  scope: WorkspaceScope,
  filter?: PromotionFilter,
  sort?: PromotionSort,
  pageSize: number = 10,
  lastDoc?: QueryDocumentSnapshot<DocumentData>
): Promise<CrudResult<PromotionListResponse>> => {
  try {
    let q: Query<DocumentData> = getCollectionRef(scope, 'promotions');

    // 필터 적용
    if (filter) {
      if (filter.month) {
        q = query(q, where('month', '==', filter.month));
      }
      if (filter.target) {
        q = query(q, where('target', '==', filter.target));
      }
      if (filter.isActive !== undefined) {
        q = query(q, where('isActive', '==', filter.isActive));
      }
    }

    // 정렬 적용 (서버 사이드 정렬)
    const sortField = sort?.field || 'createdAt';
    const sortDirection = sort?.direction || 'desc';
    q = query(q, orderBy(sortField, sortDirection));

    // 레거시는 읽기만 건너뛰고 원본/통계는 보존한다. 유효 행을 채울 때까지 커서를 전진시킨다.
    const limitSize = Math.max(100, pageSize + 1);
    const promotions: Promotion[] = [];
    let lastVisible = lastDoc;
    let hasNextPage = false;
    const searchTerm = filter?.searchTerm?.toLowerCase();
    let scanning = true;
    while (scanning) {
      const page = lastVisible ? query(q, startAfter(lastVisible), limit(limitSize)) : query(q, limit(limitSize));
      const snapshot = await getDocs(page);
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data() as Promotion;
        const matches = isCurrentPromotion(scope, data) && (!searchTerm ||
          [data.title, data.code, data.content, data.greeting, data.closing].some(value => (value || '').toLowerCase().includes(searchTerm)));
        if (matches && promotions.length === pageSize) { hasNextPage = true; break; }
        lastVisible = docSnap;
        if (matches) promotions.push({ ...data, id: docSnap.id });
      }
      scanning = !hasNextPage && snapshot.docs.length === limitSize;
    }

    return {
      success: true,
      data: {
        promotions,
        totalCount: promotions.length,
        hasNextPage,
        lastDoc: lastVisible || undefined
      }
    };
  } catch (error) {
    console.error('🔥 Firestore getPromotions error:', error);
    console.error('🔥 Error message:', (error as any)?.message);
    console.error('🔥 Error code:', (error as any)?.code);
    if ((error as any)?.message?.includes('index')) {
      console.error('🔗 Missing index detected! Check the error message for the creation link.');
    }
    return {
      success: false,
      error: handleFirebaseError(error)
    };
  }
};


export const getPromotionsByMonth = (scope: WorkspaceScope, month: string, excludePromotionId?: string): Promise<CrudResult<Promotion[]>> => result(async () => {
  const snapshot = await getDocs(query(getCollectionRef(scope, 'promotions'), where('month', '==', month), orderBy('createdAt', 'desc')));
  return snapshot.docs.filter(item => item.id !== excludePromotionId && isCurrentPromotion(scope, item.data())).map(item => ({ ...item.data(), id: item.id } as Promotion));
});
export const getActivePromotions = (scope: WorkspaceScope): Promise<CrudResult<Promotion[]>> => result(async () => {
  const response = await getPromotions(scope, { isActive: true }, undefined, 50);
  if (!response.success || !response.data) throw new Error(response.error);
  return response.data.promotions;
});
export const getPromotionCodesByMonth = (scope: WorkspaceScope, month: string): Promise<CrudResult<{ id: string; code: string; title: string }[]>> => result(async () => {
  const response = await getPromotionsByMonth(scope, month);
  if (!response.success || !response.data) throw new Error(response.error);
  return response.data.map(({ id, code, title }) => ({ id, code, title }));
});
export const getOtherProductsInfo = (scope: WorkspaceScope, ids: (string | undefined)[]): Promise<CrudResult<Record<string, { id: string; code: string; title: string }>>> => result(async () => {
  const products: Record<string, { id: string; code: string; title: string }> = {};
  await Promise.all(ids.filter((id): id is string => !!id).map(async id => {
    const response = await getPromotionById(scope, id);
    if (response.success && response.data) {
      const { code, title } = response.data;
      products[id] = { id, code, title };
    }
  }));
  return products;
});
export const findBulkCopyRecovery = (scope: WorkspaceScope, sourceMonth: string, targetMonth: string, sourceIds: string[]): Promise<CrudResult<{ randomPrefix: string | null }>> => result(
  () => workspaceApi('bulkCopyRecovery', scope, { sourceMonth, targetMonth, sourceIds })
);
export const checkBulkCopyPreview = (scope: WorkspaceScope, sourceMonth: string, targetMonth: string, randomPrefix: string, expectedSlugs: Record<string, string>): Promise<CrudResult<{ completed: boolean; conflicts: string[] }>> => result(
  () => workspaceApi('bulkCopyPreview', scope, { sourceMonth, targetMonth, randomPrefix, expectedSlugs })
);
export const bulkCopyPromotions = (scope: WorkspaceScope, sourceMonth: string, targetMonth: string, randomPrefix: string, expectedSlugs?: Record<string, string>): Promise<CrudResult<number>> => result(async () => {
  if (!expectedSlugs || !Object.keys(expectedSlugs).length) throw new Error('복사 미리보기를 다시 불러와주세요.');
  const payload = { sourceMonth, targetMonth, randomPrefix, expectedSlugs };
  try { return await workspaceApi<number>('bulkCopyFromSource', scope, payload); }
  catch (error) {
    if (error instanceof WorkspaceApiError && ['api-unreachable', 'api-invalid-response'].includes(error.code)) {
      return workspaceApi<number>('bulkCopyFromSource', scope, payload);
    }
    throw error;
  }
});
